import React from 'react';
import styled from 'styled-components';
import { Photo } from '../types/Photo';
import ProtectedImage from './ProtectedImage';

interface PhotoDetailProps {
  photo: Photo | null;
  onClose: () => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  max-height: 600px;
  overflow: hidden;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledProtectedImage = styled(ProtectedImage)`
  width: 100%;
  height: auto;
  max-height: 600px;

  img {
    object-fit: contain;
  }
`;

const Content = styled.div`
  padding: 32px;
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 16px 0;
  color: #333;
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #999;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  font-size: 16px;
  color: #333;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  white-space: pre-wrap;
  margin-bottom: 24px;
`;

const CameraDetailsSection = styled.div`
  background: #f8f8f8;
  padding: 20px;
  border-radius: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
`;

const CameraGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
`;

const PhotoDetail: React.FC<PhotoDetailProps> = ({ photo, onClose }) => {
  if (!photo) return null;

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new (Date as any)(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay isOpen={!!photo} onClick={handleOverlayClick}>
      <Modal>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ImageContainer>
          <StyledProtectedImage
            src={photo.imageURL}
            alt={photo.title}
            loading="eager"
            showWatermark={true}
            watermarkText={`© ${photo.title}`}
          />
        </ImageContainer>
        <Content>
          <Title>{photo.title}</Title>

          <MetaInfo>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>📍 {photo.location}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Date</MetaLabel>
              <MetaValue>{formatDate(photo.date)}</MetaValue>
            </MetaItem>
          </MetaInfo>

          {photo.description && (
            <Description>{photo.description}</Description>
          )}

          {photo.cameraDetails && Object.keys(photo.cameraDetails).length > 0 && (
            <CameraDetailsSection>
              <SectionTitle>Camera Details</SectionTitle>
              <CameraGrid>
                {photo.cameraDetails.make && (
                  <MetaItem>
                    <MetaLabel>Make</MetaLabel>
                    <MetaValue>{photo.cameraDetails.make}</MetaValue>
                  </MetaItem>
                )}
                {photo.cameraDetails.model && (
                  <MetaItem>
                    <MetaLabel>Model</MetaLabel>
                    <MetaValue>{photo.cameraDetails.model}</MetaValue>
                  </MetaItem>
                )}
                {photo.cameraDetails.lens && (
                  <MetaItem>
                    <MetaLabel>Lens</MetaLabel>
                    <MetaValue>{photo.cameraDetails.lens}</MetaValue>
                  </MetaItem>
                )}
                {photo.cameraDetails.focalLength && (
                  <MetaItem>
                    <MetaLabel>Focal Length</MetaLabel>
                    <MetaValue>{photo.cameraDetails.focalLength}</MetaValue>
                  </MetaItem>
                )}
                {photo.cameraDetails.aperture && (
                  <MetaItem>
                    <MetaLabel>Aperture</MetaLabel>
                    <MetaValue>{photo.cameraDetails.aperture}</MetaValue>
                  </MetaItem>
                )}
                {photo.cameraDetails.shutterSpeed && (
                  <MetaItem>
                    <MetaLabel>Shutter Speed</MetaLabel>
                    <MetaValue>{photo.cameraDetails.shutterSpeed}</MetaValue>
                  </MetaItem>
                )}
                {photo.cameraDetails.iso && (
                  <MetaItem>
                    <MetaLabel>ISO</MetaLabel>
                    <MetaValue>{photo.cameraDetails.iso}</MetaValue>
                  </MetaItem>
                )}
              </CameraGrid>
            </CameraDetailsSection>
          )}
        </Content>
      </Modal>
    </Overlay>
  );
};

export default PhotoDetail;
